/**
 * `Atbash` — the Node SDK client. Composes the Rust core (signing, identity,
 * redaction; via NAPI) with the judge / risk-engine HTTP surface.
 *
 * For `/api/risk-engine` we parse the raw JSON ourselves: that endpoint is
 * RPC-style with `?action=`, and the spec encodes its response as a single
 * `oneOf` — generator dispatch is dict-ambiguous (TierInfo vs ToolCallFull).
 * Each method knows the action it called and casts deterministically.
 */
import { randomHex } from "./random.js";

import {
  PRIVATE_CHAIN,
  PUBLIC_CHAIN,
  type ChainConfig,
} from "./chain-config.js";
import { DEFAULT_CHROMIA_NODE_URLS, DEFAULT_ENDPOINT } from "./constants.js";
import { signEncryptedToolCall } from "./encrypted-toolcall.js";
import { validateJudgeEndpoint } from "./endpoint.js";
import { AtbashAPIError, SignatureVerificationError } from "./errors.js";
import { HttpClient, type QueryValue } from "./http/client.js";
import type { components } from "./http/schema.js";
import { loadAgentFromFile } from "./keyLoader.js";
import { native } from "./native.js";
import { normalizeStatus, normalizeVerdict, pubkeyToHex } from "./normalize.js";
import { recordCall, recordDuration } from "./opentel/telemetry.js";
import { resolve } from "./userConfig.js";
import type {
  AgentAuth,
  AgentPolicy,
  AtbashLogger,
  AtbashOptions,
  ChainOpts,
  Decision,
  FromConfigOptions,
  HeldAction,
  HeldActionReview,
  JudgeOptions,
  JudgeResult,
  JudgmentStatus,
  KeyPair,
  LogToolCallOptions,
  LogToolCallResult,
  Network,
  OrgSubscription,
  RedactResult,
  TierInfo,
  ToolCallFull,
  ToolCallInput,
  ToolCallRecord,
} from "./types.js";

type JudgeRequestWire = components["schemas"]["JudgeRequest"];

/** `tc-<unix_ms>-<8 hex chars>`. */
function generateToolCallId(): string {
  return `tc-${Date.now()}-${randomHex(4)}`;
}

export class Atbash {
  readonly auth: AgentAuth;
  readonly endpoint: string;
  readonly nodeUrls: readonly string[];
  readonly blockchainRid: string;
  /** Default org name used by `auditToolCall` / `judgeAction`. */
  readonly orgName?: string;
  /** Default judge response-signing pubkey, if configured (see fromConfig). */
  readonly verifyPubKey?: string;
  /** Default org encryption key — see {@link AtbashOptions.orgEncryptionPubKey}. */
  readonly orgEncryptionPubKey?: string;
  /** When true (default), `auditToolCall` denies on any error. */
  readonly failClosed: boolean;

  /** Org key learned from the last agent-exists check, for this agent only. */
  private _orgKeyFromChain: string | null = null;
  private readonly logger: AtbashLogger;
  private readonly http: HttpClient;
  /**
   * Per-client cache of resolved chains. Keyed by orgName so repeated
   * calls don't re-hit the dashboard. Cleared by `clearChainCache()`.
   */
  private readonly _chainCache = new Map<string, ChainConfig>();
  /**
   * Cached bearer token for risk-engine / insurance read calls. Built
   * lazily as a signed `log_tool_call` tx and refreshed every 4 min so
   * server-side replay protection windows never expire it mid-session.
   */
  private _authBearer: { hex: string; issuedAt: number } | null = null;

  constructor(privkey: string, options: AtbashOptions = {}) {
    this.auth = native.loadAgent(privkey);
    this.endpoint =
      (options.endpoint ?? DEFAULT_ENDPOINT).replace(/\/+$/, "") ||
      DEFAULT_ENDPOINT;
    this.nodeUrls = options.nodeUrls
      ? [...options.nodeUrls]
      : DEFAULT_CHROMIA_NODE_URLS;
    this.blockchainRid = options.blockchainRid ?? native.DEFAULT_BLOCKCHAIN_RID;
    this.orgName = options.orgName;
    this.verifyPubKey = options.verifyPubKey;
    this.orgEncryptionPubKey = options.orgEncryptionPubKey;
    this.failClosed = options.failClosed !== false;
    this.logger = options.logger ?? {};
    this.http = new HttpClient(this.endpoint, options.timeoutMs ?? 30_000);

    if (this.endpoint !== DEFAULT_ENDPOINT) {
      this.logger.warn?.("[atbash] running on non-default judge endpoint", {
        endpoint: this.endpoint,
        verifying: this.verifyPubKey
          ? "with response-signature pubkey configured"
          : "without signature verification",
      });
    }
  }

  /**
   * Construct from resolved config: explicit overrides → env vars → the
   * `~/.config/atbash/config.json` file (see userConfig.resolve). The private
   * key comes from `agentKey` (override/env/file) or, failing that, the agent
   * key file (`~/.config/atbash/guard-client-key`). The judge endpoint is
   * validated against the trusted allowlist / self-hosted policy; a
   * self-hosted endpoint's `verifyPubKey` becomes the client default.
   */
  static fromConfig(options: FromConfigOptions = {}): Atbash {
    const validated = validateJudgeEndpoint(
      options.judge ?? { endpoint: resolve("judgeEndpoint") || undefined },
    );

    const agentKey = resolve("agentKey", options.agentKey);
    const auth: AgentAuth = agentKey
      ? native.loadAgent(agentKey)
      : loadAgentFromFile(options.keyPath);

    const blockchainRid =
      resolve("blockchainRid", options.blockchainRid) || undefined;

    return new Atbash(auth.privkey, {
      endpoint: validated.url,
      blockchainRid,
      timeoutMs: options.timeoutMs,
      nodeUrls: options.nodeUrls,
      orgName: options.orgName,
      verifyPubKey: validated.verifyPubKey ?? undefined,
      failClosed: options.failClosed,
      logger: options.logger,
    });
  }

  get pubkey(): string {
    return this.auth.pubkey;
  }

  get privkey(): string {
    return this.auth.privkey;
  }

  /* ── agent existence (/api/ai/exists) ──────────────────────────────────── */

  /**
   * `GET /api/ai/exists?pubkey=…[&network=…]` — defaults to this client's
   * pubkey. Pass `opts.network` when the caller already knows which
   * network the agent lives on (e.g. after resolving via `orgName`) so
   * the dashboard queries that chain directly instead of falling back
   * across public → private, which double-round-trips and can return
   * false negatives when the fallback chain client is misconfigured.
   */
  async checkAgentExists(
    pubkey?: string,
    opts?: { network?: Network },
  ): Promise<boolean> {
    const pk = pubkey ?? this.auth.pubkey;
    return this.track("checkAgentExists", pk, async () => {
      const query: Record<string, string> = { pubkey: pk };
      if (opts?.network) query.network = opts.network;
      const resp = await this.http.get(
        "/api/ai/exists",
        query,
        this.authHeaders(),
      );
      await this.raiseIfError(resp);
      const data = (await this.json(resp)) as {
        registered?: unknown;
        org_encryption_pubkey?: unknown;
      } | null;
      if (pk === this.auth.pubkey) {
        const key = data?.org_encryption_pubkey;
        this._orgKeyFromChain = typeof key === "string" && key ? key : null;
      }
      return Boolean(data?.registered);
    });
  }

  /* ── log_tool_call (sign-only) ─────────────────────────────────────────── */

  /**
   * Pre-flight `checkAgentExists`, then sign `log_tool_call` locally and
   * return the signed tx hex. The server broadcasts to chain.
   */
  async logToolCall(
    action: string,
    context = "",
    options: LogToolCallOptions = {},
  ): Promise<LogToolCallResult> {
    const start = performance.now();
    recordCall("logToolCall", undefined, this.auth.pubkey);

    let exists: boolean;
    try {
      exists = await this.checkAgentExists(this.auth.pubkey, {
        network: options.chainOpts?.network,
      });
    } catch (err) {
      recordDuration("logToolCall", performance.now() - start, "error");
      return { success: false, toolCallId: null, error: errorMessage(err) };
    }
    if (!exists) {
      recordDuration("logToolCall", performance.now() - start, "error");
      return {
        success: false,
        toolCallId: null,
        error:
          "Agent not registered. Onboard the agent at the dashboard before " +
          "submitting actions.",
      };
    }

    const toolCallId = generateToolCallId();
    // Derive brid from full chainOpts (network → default brid, or explicit
    // pin, or fall back to client default). Reading only .blockchainRid
    // silently drops a `{ network: "private" }` hint and signs with the
    // client's default (usually public) — breaking any judge that routes
    // by the signed brid.
    const brid = this.bridFromChainOpts(options.chainOpts);
    // The contract refuses plaintext once an org registers a key, and a caller
    // has no way to know that key — so fall back to the one the exists-check
    // above just returned.
    const orgKey =
      options.orgEncryptionPubKey ?? this.orgEncryptionPubKey ?? this._orgKeyFromChain;
    try {
      const signedHex = orgKey
        ? signEncryptedToolCall(
            toolCallId,
            action,
            context,
            options.toolName ?? "",
            options.toolArgsJson ?? "",
            orgKey,
            this.auth.privkey,
            brid,
          )
        : native.signLogToolCall(
            toolCallId,
            action,
            context,
            options.toolName ?? "",
            options.toolArgsJson ?? "",
            this.auth.privkey,
            brid,
          );
      recordDuration("logToolCall", performance.now() - start, "success");
      return { success: true, toolCallId, signedHex };
    } catch (err) {
      recordDuration("logToolCall", performance.now() - start, "error");
      return { success: false, toolCallId: null, error: errorMessage(err) };
    }
  }

  /* ── judge_action ──────────────────────────────────────────────────────── */

  /**
   * Sign log_tool_call + optionally judge_action, POST /api/v1/judge.
   *
   * `verifyPubKey` checks the `X-Atbash-Signature` header against the exact
   * response bytes via the Rust core's `verifySignature`.
   */
  async judgeAction(
    action: string,
    context = "",
    options: JudgeOptions = {},
  ): Promise<JudgeResult> {
    return this.track("judgeAction", this.auth.pubkey, () =>
      this._judgeAction(action, context, options),
    );
  }

  private async _judgeAction(
    action: string,
    context: string,
    options: JudgeOptions,
  ): Promise<JudgeResult> {
    if (!action?.trim()) {
      throw new Error("action is required and cannot be empty.");
    }

    // Resolve chain when orgName is provided. Order:
    //   1. org_networks map (authoritative post-upgrade — wins over any
    //      stale BRID hint on chainOpts).
    //   2. Caller-pinned chainOpts.blockchainRid (no map entry).
    //   3. Per-chain subscription resolution (no map, no pin).
    let chainOpts: ChainOpts | undefined = options.chainOpts;
    if (options.orgName) {
      const mapNetwork = await this.getActiveNetworkForOrg(options.orgName);
      if (mapNetwork) {
        chainOpts = { network: mapNetwork };
      } else if (!chainOpts?.blockchainRid) {
        // No map entry — hand the fetched result (null) to the fallback
        // resolver so it doesn't re-hit /api/org-network for the same org.
        const resolved = await this.resolveChainFromMap(options.orgName, null);
        chainOpts = { ...chainOpts, network: resolved.network };
      }
    }
    const brid = this.bridFromChainOpts(chainOpts);

    const logResult = await this.logToolCall(action, context, {
      toolName: options.toolName,
      toolArgsJson: options.toolArgsJson,
      chainOpts,
    });
    if (!logResult.success || !logResult.toolCallId || !logResult.signedHex) {
      throw new Error(logResult.error || "Failed to sign log_tool_call");
    }

    let signedJudgeAction: string | undefined;
    if (!options.provider) {
      const judgmentId = generateToolCallId();
      signedJudgeAction = native.signJudgeAction(
        judgmentId,
        action,
        context || "",
        "",
        this.auth.privkey,
        brid,
      );
    }

    const body: JudgeRequestWire = {
      tool_call_id: logResult.toolCallId,
      agent_pubkey: this.auth.pubkey,
      action,
      signed_log_tool_call: logResult.signedHex,
    };
    if (signedJudgeAction) body.signed_judge_action = signedJudgeAction;
    if (context) body.context = context;
    if (options.provider) body.provider = options.provider;
    if (options.toolName) body.tool_name = options.toolName;
    if (options.toolArgsJson) body.tool_args_json = options.toolArgsJson;
    if (options.model) body.model = options.model;
    // `resolved` bypasses redaction — the caller vouches for its
    // contents. See JudgeRequest.resolved in spec/openapi.yaml.
    if (options.resolved) body.resolved = options.resolved;
    if (options.mode) body.mode = options.mode;

    let resp: Response;
    try {
      resp = await this.http.post("/api/v1/judge", body);
    } catch (err) {
      throw this.transportError(err);
    }
    if (!resp.ok) throw await this.httpError(resp);

    // Read the raw bytes before any decode so signature verification runs
    // against exactly what the server signed.
    const bodyBytes = Buffer.from(await resp.arrayBuffer());
    const verifyPubKey = options.verifyPubKey ?? this.verifyPubKey;
    if (verifyPubKey !== undefined) {
      const sig = resp.headers.get("X-Atbash-Signature");
      if (!sig) {
        throw new SignatureVerificationError(
          "missing X-Atbash-Signature header",
        );
      }
      let ok: boolean;
      try {
        ok = native.verifySignature(bodyBytes, sig, verifyPubKey);
      } catch (err) {
        throw new SignatureVerificationError(
          `signature verification threw: ${errorMessage(err)}`,
        );
      }
      if (!ok) {
        throw new SignatureVerificationError(
          "signature does not verify against configured verifyPubKey",
        );
      }
    }

    const data = parseJson(bodyBytes) as Record<string, unknown>;
    // `score` only appears on memory-scan responses. Coerce when
    // present; leave undefined otherwise so callers can distinguish
    // "absent" from "invalid" (NaN would collapse both).
    const rawScore = data.score;
    const score =
      typeof rawScore === "number" && Number.isInteger(rawScore) && rawScore >= 1 && rawScore <= 10
        ? rawScore
        : undefined;
    return {
      verdict: normalizeVerdict(data.verdict),
      actionType: String(data.action_type ?? ""),
      reason: String(data.reason ?? ""),
      confidence: Number(data.confidence ?? 0),
      provider: String(data.provider ?? ""),
      latencyMs: Number(data.latency_ms ?? 0),
      toolCallId: String(data.tool_call_id ?? logResult.toolCallId),
      onChain: Boolean(data.on_chain),
      enforced: Boolean(data.enforced),
      enforcementMode: String(data.enforcement_mode ?? ""),
      status: String(data.status ?? ""),
      score,
    };
  }

  /* ── audit_tool_call (redact → judge → decision) ───────────────────────── */

  /**
   * High-level guard: redact secrets, submit for judgement, and collapse the
   * result into an allow/deny `Decision`. Fails closed by default — any error
   * (judge unreachable, unrecognized verdict) denies unless `failClosed` is
   * explicitly false.
   */
  async auditToolCall(input: ToolCallInput): Promise<Decision> {
    const toolName = input.toolName || "unknown";

    // Redact secret-shaped values BEFORE signing so they never reach the
    // signed bytes, the request body, the on-chain log, or the LLM prompt.
    const argsRedaction = native.redactSecrets(stringifyArgs(input.args));
    const ctxRedaction = native.redactSecrets(input.context ?? toolName);
    const argsJson = argsRedaction.redacted;
    const actionText = truncate(argsJson);
    const contextText = ctxRedaction.redacted;
    const totalRedactions =
      argsRedaction.found.length + ctxRedaction.found.length;
    if (totalRedactions > 0) {
      const kinds = [
        ...new Set([
          ...argsRedaction.found.map((f) => f.kind),
          ...ctxRedaction.found.map((f) => f.kind),
        ]),
      ];
      this.logger.warn?.("[atbash] redacted secrets before judge call", {
        tool: toolName,
        count: totalRedactions,
        kinds,
      });
    }

    try {
      this.logger.info?.("[atbash] judge API called", { tool: toolName });
      const result = await this.judgeAction(actionText, contextText, {
        toolName,
        toolArgsJson: argsJson,
        orgName: this.orgName,
        // Passed through unredacted — caller vouches for the contents.
        resolved: input.resolved,
      });

      // AUDIT tier — server returns no verdict (log only, no AI enforcement).
      //
      // A missing verdict on its own must NOT mean allow. `{}`, a truncated or
      // empty body, a buggy judge, or a compromised endpoint all normalize to
      // "No verdict" too, and treating those as ALLOW would silently disable
      // the guardrail exactly when it is least trustworthy. Require the
      // server's explicit AUDIT-tier marker (`status: "logged"`); anything else
      // with an absent verdict fails closed via `fail()`.
      if (result.verdict === "No verdict") {
        if (result.status !== "logged") {
          return this.fail(
            `judge returned no verdict without an audit-tier marker (status: ${
              result.status || "absent"
            })`,
            result.toolCallId,
          );
        }
        return {
          allow: true,
          verdict: "ALLOW",
          reason:
            result.reason ||
            "audit tier — request logged on-chain, no AI enforcement",
          toolCallId: result.toolCallId,
        };
      }

      const action = result.actionType;
      if (action === "block") {
        return {
          allow: false,
          verdict: "BLOCK",
          reason: result.reason,
          toolCallId: result.toolCallId,
        };
      }
      if (action === "hold_for_user_confirm") {
        return {
          allow: false,
          verdict: "HOLD",
          reason: result.reason || "held for human confirmation",
          toolCallId: result.toolCallId,
        };
      }
      if (action === "allow") {
        // If verdict conflicts with action_type (server confusion), respect the
        // more restrictive signal rather than blindly allowing.
        if (result.verdict === "HOLD") {
          return {
            allow: false,
            verdict: "HOLD",
            reason: result.reason,
            toolCallId: result.toolCallId,
          };
        }
        if (result.verdict === "BLOCK") {
          return {
            allow: false,
            verdict: "BLOCK",
            reason: result.reason,
            toolCallId: result.toolCallId,
          };
        }
        return {
          allow: true,
          verdict: "ALLOW",
          reason: result.reason,
          toolCallId: result.toolCallId,
        };
      }

      return this.fail(
        "unrecognized action_type from judge",
        result.toolCallId,
      );
    } catch (err) {
      const message = errorMessage(err);
      this.logger.warn?.("[atbash] judge API failed", { reason: message });
      return this.fail(message);
    }
  }

  private fail(reason: string, toolCallId?: string): Decision {
    return { allow: !this.failClosed, verdict: "ERROR", reason, toolCallId };
  }

  /* ── judgment status ───────────────────────────────────────────────────── */

  async getJudgmentStatus(
    judgmentId: string,
    agentPubkey?: string,
  ): Promise<JudgmentStatus> {
    const pk = agentPubkey ?? this.auth.pubkey;
    return this.track("getJudgmentStatus", pk, async () => {
      const resp = await this.http.get(
        "/api/v1/judge",
        { tool_call_id: judgmentId, agent_pubkey: pk },
        this.authHeaders(),
      );
      await this.raiseIfError(resp);
      const data = ((await this.json(resp)) ?? {}) as Record<string, unknown>;
      return {
        status: normalizeStatus(data.status),
        verdict: normalizeVerdict(data.verdict),
        reason: String(data.reason ?? ""),
        judgmentId: String(data.judgmentId ?? judgmentId),
        onChain: optBool(data.onChain),
        cached: optBool(data.cached),
        responseTimeMs: optNumber(data.responseTimeMs),
      };
    });
  }

  /* ── risk-engine queries (action-dispatched GET) ───────────────────────── */

  getToolCalls(maxCount: number): Promise<ToolCallRecord[]> {
    return this.track("getToolCalls", undefined, () =>
      this.riskEngineRecords("tool-calls", { limit: maxCount }),
    );
  }

  getOrgToolCalls(
    orgName: string,
    maxCount: number,
  ): Promise<ToolCallRecord[]> {
    return this.track("getOrgToolCalls", undefined, () =>
      this.riskEngineRecords("org-tool-calls", {
        org: orgName,
        limit: maxCount,
      }),
    );
  }

  getAgentToolCalls(
    agentPubkey: string,
    maxCount: number,
  ): Promise<ToolCallRecord[]> {
    return this.track("getAgentToolCalls", agentPubkey, () =>
      this.riskEngineRecords("agent-tool-calls", {
        agent: agentPubkey,
        limit: maxCount,
      }),
    );
  }

  async getToolCallCount(): Promise<number> {
    return this.track("getToolCallCount", undefined, async () => {
      const raw = await this.riskEngineGet("tool-call-count", {});
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    });
  }

  async getToolCallFull(toolCallId: string): Promise<ToolCallFull | null> {
    return this.track("getToolCallFull", undefined, async () => {
      const raw = await this.riskEngineGet("tool-call-full", {
        tool_call_id: toolCallId,
      });
      if (!isRecord(raw)) return null;
      return toToolCallFull(raw);
    });
  }

  async getOrgTierInfo(orgName: string): Promise<TierInfo | null> {
    return this.track("getOrgTierInfo", undefined, async () => {
      const raw = await this.riskEngineGet("org-tier-info", { org: orgName });
      if (!isRecord(raw)) return null;
      return {
        orgName: String(raw.org_name ?? ""),
        tier: String(raw.tier ?? ""),
        verdictEnabled: Boolean(raw.verdict_enabled),
        enforcementEnabled: Boolean(raw.enforcement_enabled),
      };
    });
  }

  async getPendingHeldActions(
    orgName: string,
    maxCount: number,
  ): Promise<HeldAction[]> {
    return this.track("getPendingHeldActions", undefined, async () => {
      const raw = await this.riskEngineGet("pending-held-actions", {
        org: orgName,
        limit: maxCount,
      });
      if (!Array.isArray(raw)) return [];
      return raw.map((item) => toHeldAction(item as Record<string, unknown>));
    });
  }

  async getHeldActionReviews(
    orgName: string,
    maxCount: number,
  ): Promise<HeldActionReview[]> {
    return this.track("getHeldActionReviews", undefined, async () => {
      const raw = await this.riskEngineGet("held-action-reviews", {
        org: orgName,
        limit: maxCount,
      });
      if (!Array.isArray(raw)) return [];
      return raw.map((item) =>
        toHeldActionReview(item as Record<string, unknown>),
      );
    });
  }

  /* ── risk-engine batched (action-dispatched POST) ──────────────────────── */

  getAgentDetail(agentPubkey: string): Promise<Record<string, unknown>> {
    return this.track("getAgentDetail", agentPubkey, () =>
      this.riskEnginePost({ action: "agent-detail-batch", agent: agentPubkey }),
    );
  }

  async getAgentPolicy(agentPubkey: string): Promise<AgentPolicy> {
    return this.track("getAgentPolicy", agentPubkey, async () => {
      const raw = await this.riskEnginePost({
        action: "agent-policy-batch",
        agent: agentPubkey,
      });
      return {
        policy: String(raw.policy ?? ""),
        isJailed: Boolean(raw.is_jailed),
        isCustom: Boolean(raw.is_custom),
        defaultPolicy: String(raw.default_policy ?? ""),
      };
    });
  }

  /* ── safety stats (/api/insurance?action=safety-stats) ─────────────────── */

  async getSafetyStats(): Promise<Record<string, unknown>> {
    return this.track("getSafetyStats", undefined, async () => {
      const resp = await this.http.get(
        "/api/insurance",
        { action: "safety-stats" },
        this.authHeaders(),
      );
      await this.raiseIfError(resp);
      const data = ((await this.json(resp)) ?? {}) as Record<string, unknown>;
      // TS unwraps `.data` when present.
      if (isRecord(data.data)) return data.data;
      return data;
    });
  }

  /* ── chain resolution (org → chain) ────────────────────────────────────── */

  /**
   * Org's subscription on a specific chain. The `network` arg selects
   * which chain to query; without it, the dashboard picks the default.
   * Returns null when the org has no record on that chain.
   */
  async getOrgSubscription(
    orgName: string,
    network?: Network,
  ): Promise<OrgSubscription | null> {
    return this.track("getOrgSubscription", undefined, async () => {
      const params: Record<string, QueryValue> = { org: orgName };
      if (network) params.network = network;
      const raw = await this.riskEngineGet("org-subscription", params);
      if (!isRecord(raw)) return null;
      return coerceOrgSubscription(raw, orgName);
    });
  }

  /**
   * Read the org's active network from the dashboard's off-chain
   * `org_networks` map. The map is the authoritative source after a
   * plan switch — subscription rows on the source chain go stale, but
   * the map is updated on every assign. Returns null when there's no
   * entry (caller falls back to per-chain subscription resolution).
   */
  async getActiveNetworkForOrg(orgName: string): Promise<Network | null> {
    try {
      const resp = await this.http.get(
        "/api/org-network",
        { org: orgName },
        this.authHeaders(),
      );
      if (resp.status !== 200) return null;
      const data = (await this.json(resp)) as {
        network?: string | null;
      } | null;
      if (data?.network === "public" || data?.network === "private") {
        return data.network;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Resolve which chain an org's actions should run against. Cached
   * per-client by orgName. Resolution order:
   *   1. `org_networks` map (authoritative).
   *   2. Per-chain subscription fallback — public + private records
   *      are fetched in parallel, with `is_private_blockchain` and
   *      `assigned_at` reconciling mixed states.
   * Defaults to the public chain when nothing else resolves.
   */
  async resolveChainForOrg(orgName: string): Promise<ChainConfig> {
    const cached = this._chainCache.get(orgName);
    if (cached) return cached;
    const mapNetwork = await this.getActiveNetworkForOrg(orgName);
    return this.resolveChainFromMap(orgName, mapNetwork);
  }

  /**
   * Resolve a chain given an already-fetched `org_networks` map result.
   * Split out from {@link resolveChainForOrg} so callers that have already
   * queried the map (the judge path) don't fetch /api/org-network twice.
   * Caches per orgName like its caller.
   */
  private async resolveChainFromMap(
    orgName: string,
    mapNetwork: Network | null,
  ): Promise<ChainConfig> {
    const cached = this._chainCache.get(orgName);
    if (cached) return cached;

    if (mapNetwork) {
      const chain = mapNetwork === "private" ? PRIVATE_CHAIN : PUBLIC_CHAIN;
      this._chainCache.set(orgName, chain);
      return chain;
    }

    // Fallback: both subscription rows can exist after a plan switch
    // because `admin_assign_subscription` writes only to the destination
    // chain. Reconcile with `is_private_blockchain` + `assigned_at`.
    try {
      const [pubSub, privSub] = await Promise.all([
        this.getOrgSubscription(orgName, "public").catch(() => null),
        this.getOrgSubscription(orgName, "private").catch(() => null),
      ]);

      if (pubSub?.is_private_blockchain) {
        this._chainCache.set(orgName, PRIVATE_CHAIN);
        return PRIVATE_CHAIN;
      }
      if (pubSub && privSub) {
        const chain =
          privSub.assigned_at > pubSub.assigned_at
            ? PRIVATE_CHAIN
            : PUBLIC_CHAIN;
        this._chainCache.set(orgName, chain);
        return chain;
      }
      if (pubSub) {
        this._chainCache.set(orgName, PUBLIC_CHAIN);
        return PUBLIC_CHAIN;
      }
      if (privSub?.is_private_blockchain) {
        this._chainCache.set(orgName, PRIVATE_CHAIN);
        return PRIVATE_CHAIN;
      }
    } catch {
      // Fall through to public default if subscription lookup fails.
    }
    this._chainCache.set(orgName, PUBLIC_CHAIN);
    return PUBLIC_CHAIN;
  }

  /** Drop any cached chain resolutions. Useful in tests. */
  clearChainCache(): void {
    this._chainCache.clear();
  }

  /* ── internals ─────────────────────────────────────────────────────────── */

  /**
   * Wrap an SDK method body in telemetry — records the call at start
   * and a success/error duration at end. Re-throws on failure so the
   * caller sees the original exception. Pass `agentPubkey` when the
   * method is keyed to a specific agent; tracked methods that don't
   * depend on an agent (read queries) pass `undefined`.
   */
  private async track<T>(
    name: string,
    agentPubkey: string | undefined,
    fn: () => Promise<T>,
  ): Promise<T> {
    const start = performance.now();
    recordCall(name, undefined, agentPubkey);
    try {
      const result = await fn();
      recordDuration(name, performance.now() - start, "success");
      return result;
    } catch (err) {
      recordDuration(name, performance.now() - start, "error");
      throw err;
    }
  }

  /**
   * Pick the BRID for a given per-call chain override. `blockchainRid`
   * takes precedence; otherwise `network` maps to one of the known
   * chains; otherwise the client's default.
   */
  private bridFromChainOpts(chainOpts?: ChainOpts): string {
    if (chainOpts?.blockchainRid) return chainOpts.blockchainRid;
    if (chainOpts?.network === "private") return PRIVATE_CHAIN.blockchainRid;
    if (chainOpts?.network === "public") return PUBLIC_CHAIN.blockchainRid;
    return this.blockchainRid;
  }

  /**
   * Get-or-create a Bearer token for dashboard reads. The token is a
   * signed `log_tool_call` op (locally signed, never submitted) — the
   * dashboard verifies the signature against the agent's pubkey. Cached
   * for 4 minutes; refreshed after that so a long-lived client never
   * trips the server's replay window.
   */
  private getAuthBearer(): string {
    const now = Date.now();
    if (this._authBearer && now - this._authBearer.issuedAt < 4 * 60 * 1000) {
      return this._authBearer.hex;
    }
    const nonce = `auth-${now.toString(36)}-${randomHex(4)}`;
    const hex = native.signLogToolCall(
      nonce,
      `auth:${now}`,
      "",
      "auth-bearer",
      "",
      this.auth.privkey,
      this.blockchainRid,
    );
    this._authBearer = { hex, issuedAt: now };
    return hex;
  }

  private authHeaders(): Record<string, string> {
    return { Authorization: `Bearer ${this.getAuthBearer()}` };
  }

  private async riskEngineGet(
    action: string,
    params: Record<string, QueryValue>,
  ): Promise<unknown> {
    let resp: Response;
    try {
      resp = await this.http.get(
        "/api/risk-engine",
        { action, ...params },
        this.authHeaders(),
      );
    } catch (err) {
      throw this.transportError(err);
    }
    if (resp.status !== 200) throw await this.httpError(resp);
    return this.json(resp);
  }

  private async riskEnginePost(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    let resp: Response;
    try {
      resp = await this.http.post("/api/risk-engine", body, this.authHeaders());
    } catch (err) {
      throw this.transportError(err);
    }
    if (resp.status !== 200) throw await this.httpError(resp);
    const data = await this.json(resp);
    return isRecord(data) ? data : {};
  }

  private async riskEngineRecords(
    action: string,
    params: Record<string, QueryValue>,
  ): Promise<ToolCallRecord[]> {
    const raw = await this.riskEngineGet(action, params);
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => toToolCallRecord(item as Record<string, unknown>));
  }

  private async raiseIfError(resp: Response): Promise<void> {
    if (resp.ok) return;
    throw await this.httpError(resp);
  }

  /** Wrap a failed HTTP *response* (non-2xx / non-200) as an AtbashAPIError. */
  private async httpError(resp: Response): Promise<AtbashAPIError> {
    return new AtbashAPIError(
      resp.status,
      await safeText(resp),
      resp.statusText,
      this.endpoint,
    );
  }

  /** Wrap a *transport* failure (fetch threw, no response) as an AtbashAPIError. */
  private transportError(err: unknown): AtbashAPIError {
    return new AtbashAPIError(0, errorMessage(err), "", this.endpoint);
  }

  private async json(resp: Response): Promise<unknown> {
    const text = await safeText(resp);
    if (!text) return null;
    const parsed = tryParseJson(text);
    return parsed === undefined ? null : parsed;
  }

  /* ── crypto / redaction passthroughs (Rust core) ───────────────────────── */

  static generateKeypair(): KeyPair {
    return native.generateKeypair();
  }

  static isValidPrivateKey(hex: string): boolean {
    return native.isValidPrivateKey(hex);
  }

  static derivePublicKey(privkey: string): string {
    return native.derivePublicKey(privkey);
  }

  static redactSecrets(text: string): RedactResult {
    return native.redactSecrets(text);
  }

  static normalizeForMatching(text: string): string {
    return native.normalizeForMatching(text);
  }

  static containsEvasionCharacters(text: string): boolean {
    return native.containsEvasionCharacters(text);
  }
}

/* ── record converters ─────────────────────────────────────────────────── */

/** Fields shared by ToolCallRecord and ToolCallFull. */
function baseToolCall(raw: Record<string, unknown>) {
  return {
    toolCallId: String(raw.tool_call_id ?? ""),
    agentPubkey: pubkeyToHex(raw.agent_pubkey),
    toolName: String(raw.tool_name ?? ""),
    commandText: String(raw.command_text ?? ""),
    contextText: String(raw.context_text ?? ""),
    orgName: String(raw.org_name ?? ""),
  };
}

function toToolCallRecord(raw: Record<string, unknown>): ToolCallRecord {
  return {
    ...baseToolCall(raw),
    toolArgsJson: String(raw.tool_args_json ?? ""),
    rowid: Number(raw.rowid ?? 0),
  };
}

function toToolCallFull(raw: Record<string, unknown>): ToolCallFull {
  return {
    ...baseToolCall(raw),
    toolArgsJson: optString(raw.tool_args_json),
    createdAt: optNumber(raw.created_at),
    actionType: optString(raw.action_type),
    resultStatus: optString(raw.result_status),
    verdictColor: optString(raw.verdict_color),
    verdictReason: optString(raw.verdict_reason),
    verdictSource: optString(raw.verdict_source),
    verdictResponseTimeMs: optNumber(raw.verdict_response_time_ms),
  };
}

function toHeldAction(raw: Record<string, unknown>): HeldAction {
  return {
    judgmentId: String(raw.judgment_id ?? ""),
    agentPubkey: pubkeyToHex(raw.agent_pubkey),
    actionText: String(raw.action_text ?? ""),
    actionContext: String(raw.action_context ?? ""),
    verdict: normalizeVerdict(raw.verdict),
    reason: String(raw.reason ?? ""),
    createdAt: Number(raw.created_at ?? 0),
  };
}

function toHeldActionReview(raw: Record<string, unknown>): HeldActionReview {
  return {
    judgmentId: String(raw.judgment_id ?? ""),
    actionText: String(raw.action_text ?? ""),
    status: String(raw.status ?? ""),
    reviewNote: String(raw.review_note ?? ""),
    reviewedAt: Number(raw.reviewed_at ?? 0),
    createdAt: Number(raw.created_at ?? 0),
    reviewedBy:
      optString(raw.reviewed_by) || pubkeyToHex(raw.reviewed_by) || undefined,
  };
}

function coerceOrgSubscription(
  raw: Record<string, unknown>,
  orgName: string,
): OrgSubscription {
  return {
    org_name: String(raw.org_name ?? orgName),
    subscription_name: String(raw.subscription_name ?? ""),
    agent_number: Number(raw.agent_number ?? 0),
    is_private_blockchain: Boolean(raw.is_private_blockchain),
    monthly_price: Number(raw.monthly_price ?? 0),
    yearly_price: Number(raw.yearly_price ?? 0),
    duration_months: Number(raw.duration_months ?? 0),
    assigned_at: Number(raw.assigned_at ?? 0),
    expires_at: Number(raw.expires_at ?? 0),
    is_active: Boolean(raw.is_active),
  };
}

/* ── small helpers ─────────────────────────────────────────────────────── */

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function optString(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  return typeof v === "string" ? v : String(v);
}

function optNumber(v: unknown): number | undefined {
  if (v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function optBool(v: unknown): boolean | undefined {
  if (v === null || v === undefined) return undefined;
  return Boolean(v);
}

/** Parse JSON, returning `undefined` (not throwing) on malformed input. */
function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function parseJson(bytes: Buffer): unknown {
  const parsed = tryParseJson(bytes.toString("utf-8"));
  return parsed === undefined ? {} : parsed;
}

async function safeText(resp: Response): Promise<string> {
  try {
    return await resp.text();
  } catch {
    return "";
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function stringifyArgs(args: unknown): string {
  if (args === null || args === undefined) return "";
  if (typeof args === "string") return args;
  try {
    return JSON.stringify(args);
  } catch {
    return String(args);
  }
}

const MAX_ACTION_LEN = 4000;
function truncate(text: string): string {
  if (text.length <= MAX_ACTION_LEN) return text;
  return text.slice(0, MAX_ACTION_LEN) + "…";
}
